import { GAME } from "../config/gameConfig";
import type { Game } from "../game/Game";
import type { GameEvent } from "../game/types";

type Buttons = { swap: boolean; drop: boolean; rotateLeft: boolean; rotateRight: boolean; menu: boolean; resetView: boolean };
const released = (): Buttons => ({ swap: false, drop: false, rotateLeft: false, rotateRight: false, menu: false, resetView: false });

/** W3C standard mapping, including Xbox Series X/S over Bluetooth. */
export class GamepadInput {
  private readonly windows = new Set<Window>();
  private pad: Gamepad | null = null;
  private previous = released();
  private horizontal = 0;
  private axisX = 0;
  private axisY = 0;
  private horizontalSince = 0;
  private horizontalRepeat = 0;
  private softHeld = false;
  private softRepeat = 0;
  private awaitingNeutral = true;
  private lastStatus = "";
  private lastInput = "";
  private lastUpdate: number | null = null;

  constructor(
    private readonly game: Game,
    private readonly canRestart: () => boolean,
    private readonly canControl: () => boolean,
    private readonly onStatus: (message: string) => void,
    private readonly onInput: (message: string) => void,
    private readonly onLook: (x: number, y: number, seconds: number) => void = () => {},
    private readonly onResetView: () => void = () => {},
  ) { this.attachWindow(window); }

  attachWindow(target: Window): void { this.windows.add(target); }

  update(now: number): void {
    const seconds = this.lastUpdate === null ? 0 : Math.max(0, Math.min(100, now - this.lastUpdate)) / 1000;
    this.lastUpdate = now;
    for (const target of this.windows) if (target.closed) this.windows.delete(target);
    const focused = [...this.windows].find(target => target.document.hasFocus());
    const reader = focused ?? window;
    if (!reader.navigator.getGamepads) {
      this.disconnect("このブラウザーはゲームパッドに対応していません");
      return;
    }
    let pads: Gamepad[];
    try { pads = Array.from(reader.navigator.getGamepads()).filter((pad): pad is Gamepad => Boolean(pad?.connected)); }
    catch { this.disconnect("ゲームパッドを読み取れません — ChromeでlocalhostまたはHTTPSを開いてください"); return; }
    const supported = pads.filter(pad => pad.mapping === "standard");
    const pad = supported.find(pad => pad.index === this.pad?.index && pad.id === this.pad.id) ?? supported[0];
    if (!pad) {
      this.disconnect(pads.length ? "標準マッピング未対応 — Xboxコントローラーを接続し直してください" : "未検出 — Bluetooth接続後、この画面でAボタンを押してください");
      return;
    }
    if (!this.pad || this.pad.index !== pad.index || this.pad.id !== pad.id) this.reset();
    this.pad = pad;
    this.status(`接続済み: ${pad.id} · 標準マッピング`);
    const pressed = (index: number) => Boolean(pad.buttons[index]?.pressed || (pad.buttons[index]?.value ?? 0) > 0.5);
    this.axisX = this.axisDirection(pad.axes[0] ?? 0, this.axisX);
    this.axisY = this.axisDirection(pad.axes[1] ?? 0, this.axisY);
    const dpadX = Number(pressed(15)) - Number(pressed(14));
    const horizontal = pressed(14) || pressed(15) ? dpadX : this.axisX;
    const soft = pressed(13) || this.axisY === 1;
    const buttons: Buttons = {
      swap: pressed(0), drop: pressed(3) || pressed(12),
      rotateLeft: pressed(2) || pressed(4), rotateRight: pressed(1) || pressed(5), menu: pressed(9), resetView: pressed(11),
    };
    const lookX = pad.axes[2] ?? 0, lookY = pad.axes[3] ?? 0;
    const looking = Math.hypot(lookX, lookY) > 0.18;
    const labels = [
      buttons.swap && "A: 前後切替", buttons.drop && "↑ / Y: 即落下",
      buttons.rotateLeft && "X / LB: 左回転", buttons.rotateRight && "B / RB: 右回転",
      buttons.menu && "Menu: 開始・ポーズ", horizontal < 0 && "←", horizontal > 0 && "→", soft && "↓: 加速",
      looking && "右スティック: 視点回転", buttons.resetView && "R3: 視点リセット",
    ].filter(Boolean);
    this.input(labels.length ? `入力: ${labels.join(" / ")}` : "入力: ニュートラル");
    const neutral = !Object.values(buttons).some(Boolean) && !horizontal && !soft && !looking;
    if (!focused || !this.canControl()) {
      this.awaitingNeutral = true;
      this.previous = buttons;
      this.horizontal = 0; this.softHeld = false;
      return;
    }
    // Focus changes, reconnects and leaving diagnostics must not replay held
    // buttons or drop a newly spawned piece. Release before accepting input.
    if (this.awaitingNeutral) {
      this.previous = buttons;
      if (neutral) this.awaitingNeutral = false;
      return;
    }
    const edge = (key: keyof Buttons) => buttons[key] && !this.previous[key];
    const menu = edge("menu"), swap = edge("swap"), drop = edge("drop");
    const rotateLeft = edge("rotateLeft"), rotateRight = edge("rotateRight");
    const resetView = edge("resetView");
    this.previous = buttons;
    if (resetView) this.onResetView();
    else this.onLook(lookX, lookY, seconds);
    if (this.game.phase === "title" || this.game.phase === "game-over") {
      if ((swap || menu) && this.canRestart()) this.game.start(this.game.mode);
      this.horizontal = 0; this.softHeld = false;
      return;
    }
    if (menu) { this.game.togglePause(); this.horizontal = 0; this.softHeld = false; return; }
    if (this.game.phase !== "playing") { this.horizontal = 0; this.softHeld = false; return; }
    if (horizontal !== this.horizontal) {
      this.horizontal = horizontal;
      this.horizontalSince = this.horizontalRepeat = now;
      if (horizontal) this.game.moveHorizontal(horizontal as -1 | 1);
    } else if (horizontal && now - this.horizontalSince >= GAME.dasMs && now - this.horizontalRepeat >= GAME.arrMs) {
      this.game.moveHorizontal(horizontal as -1 | 1);
      this.horizontalRepeat = now;
    }
    if (soft && (!this.softHeld || now - this.softRepeat >= GAME.arrMs)) {
      this.game.softDrop(); this.softRepeat = now;
    }
    this.softHeld = soft;
    // One discrete action per frame: a simultaneous rotation/drop cannot act
    // on the next piece spawned by a hard drop.
    if (drop) this.game.hardDrop();
    else if (swap) this.game.switchLayer();
    else if (rotateLeft !== rotateRight) this.game.rotate(rotateLeft ? -1 : 1);
  }

  handle(events: readonly GameEvent[]): void {
    const actuator = this.pad?.vibrationActuator;
    if (!actuator?.playEffect || ![...this.windows].some(target => !target.closed && target.document.hasFocus())) return;
    const clear = events.some(event => event.type === "line-clear");
    if (!clear && !events.some(event => event.type === "lock")) return;
    try {
      void actuator.playEffect("dual-rumble", {
        duration: clear ? 160 : 40, startDelay: 0,
        strongMagnitude: clear ? 0.4 : 0.12, weakMagnitude: clear ? 0.65 : 0.22,
      }).catch(() => {});
    } catch { /* Bluetooth/browser combinations can omit vibration support. */ }
  }

  private axisDirection(value: number, previous: number): number {
    if (value >= 0.45) return 1;
    if (value <= -0.45) return -1;
    if (previous === 1 && value > 0.25) return 1;
    if (previous === -1 && value < -0.25) return -1;
    return 0;
  }

  private reset(): void {
    this.pad = null; this.previous = released(); this.horizontal = this.axisX = this.axisY = 0;
    this.softHeld = false; this.awaitingNeutral = true;
  }
  private disconnect(message: string): void { this.reset(); this.status(message); this.input("入力: —"); }
  private status(message: string): void { if (message !== this.lastStatus) { this.lastStatus = message; this.onStatus(message); } }
  private input(message: string): void { if (message !== this.lastInput) { this.lastInput = message; this.onInput(message); } }
}
